import {ApiClient, createApiClient} from '@simpleforyou/client-core';
import {BaseApi} from '@/common/api/BaseApi.js';
import {useAuth} from '@/composables/useAuth.js';
import {useStore} from '@/common/store/store.js';
import {applyLockout, isAdminTenantFE} from '@/composables/common/maintenance/useMaintenance';
import {IMaintenanceLockout} from '@/types/components/maintenance/IMaintenance';
import axios, {AxiosError, InternalAxiosRequestConfig} from 'axios';

axios.defaults.withCredentials = true;

let apiClient: ReturnType<typeof createApiClient>;

export function useApiClient(): ApiClient {
  if (!apiClient) {
    apiClient = createApiClient({
      baseURL: BaseApi.ROOT_URL,
      withCredentials: true,
      getAccessToken: () => useStore().accessToken,
      refresh: async () => {
        const {refresh} = useAuth();
        return await refresh();
      },
    });

    apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (token && config.headers) {
        config.headers['X-XSRF-TOKEN'] = token;
      }
      return config;
    });

    // Hard cut on maintenance lockout: a 503 with phase=active means the
    // backend rejected the request because a non-admin tenant is mid-window.
    // Drop the session and flip the lockout screen on immediately — no extra
    // roundtrip needed since the server already sent us the window payload.
    // Skip on admin tenant: the guard never blocks app.community-coaching.com,
    // so a 503 there is a backend misconfiguration. Tearing down the session
    // would crash MainLayout (logoUrl reads userData.customer) without ever
    // showing the screen (admin tenant doesn't render it).
    apiClient.interceptors.response.use(
      (response) => response,
      (error: AxiosError<IMaintenanceLockout & {errorCode?: string}>) => {
        if (
          error.response?.status === 503
          && error.response.data?.phase === 'active'
          && !isAdminTenantFE()
        ) {
          useStore().logout();
          applyLockout(error.response.data);
        }

        // Trial limits come back as a 403 with a machine-readable errorCode
        // (TRIAL_LIMIT_*). Stash it on the store so the app can surface a
        // localized message; the originating call still rejects as usual.
        const errorCode = error.response?.data?.errorCode;
        if (error.response?.status === 403 && errorCode?.startsWith('TRIAL_LIMIT_')) {
          useStore().setTrialLimitCode(errorCode);
        }

        return Promise.reject(error);
      }
    );
  }

  return new ApiClient(apiClient);
}
